// frontend/src/pages/checkout/CheckoutPage.tsx
//
// 托管收银台主页面（路由 /checkout/:id）。
//
// 两条数据流：
//   1. 新会话流（URL 带 ?token=）：GET /public/checkout-session?token= 渲染脱敏会话
//      （商户名、商品行、小计/税/合计、订阅周期文案、setup 绑卡文案），
//      确认按钮调 POST /public/checkout-session/confirm?token=；
//      成功复用 notifyCheckoutResult 桥接 + 跳 success 页；
//      返回带 client_secret/next_action 时显示"需要进一步验证"（本期不内嵌 Element）。
//   2. 旧 payment-intent 流（无 token）：GET /checkout/:id、POST /checkout/:id/confirm、
//      POST /checkout/:id/cancel，保持不变。

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Form, Input, message } from 'antd';
import {
  CreditCardOutlined,
  LockOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { QRCodeSVG } from 'qrcode.react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faWeixin,
  faAlipay,
  faQq,
  faBitcoin,
  faApplePay,
  faAmazonPay,
  faPaypal,
  faCcVisa,
  faCcMastercard,
  faCcAmex,
  faGooglePay,
} from '@fortawesome/free-brands-svg-icons';
import {
  faCreditCard,
  faBuildingColumns,
  faMoneyBill,
  faQrcode,
} from '@fortawesome/free-solid-svg-icons';
import api from '../../services/api';
import CatalogService, {
  PublicCheckoutSession,
  CheckoutMethodOption,
  OwnChannelResult,
} from '../../services/catalog';
import CheckoutShell from './CheckoutShell';
import {
  CheckoutLang,
  CheckoutTheme,
  t,
  detectLang,
  detectTheme,
  stripeLocale,
  LANG_STORAGE_KEY,
  THEME_STORAGE_KEY,
} from './i18n';
import { notifyCheckoutResult, isEmbedded } from '../../utils/checkoutBridge';
import './checkout.css';

// 最小 Stripe.js 类型声明（仅声明本页用到的部分，避免新增 npm 依赖与 tsc 报错）。
// 注意：window.Stripe 的全局类型已在别处声明，这里不再重复 declare global，
// 仅在使用处把实例 cast 成本页需要的 StripeInstance 形态。
type StripeElement = {
  mount: (selector: string | HTMLElement) => void;
  unmount: () => void;
  on: (event: string, handler: (ev: any) => void) => void;
  destroy?: () => void;
};
type StripeElements = { create: (type: string, options?: any) => StripeElement };

// Apple/Google Pay 钱包请求对象（PaymentRequest）。canMakePayment 决定钱包按钮是否出现。
type StripePaymentRequest = {
  canMakePayment: () => Promise<{ applePay?: boolean; googlePay?: boolean } | null>;
  on: (event: string, handler: (ev: any) => void) => void;
};

type StripeConfirmResult = {
  error?: { message?: string };
  paymentIntent?: { id: string; status: string };
};

// Payment Element 确认（「更多支付方式」用）：confirmParams.return_url 用于跳转类方式（3DS/Klarna/ACH 等）
// 返回后由现有 redirect_status 结果态处理；redirect:'if_required' 时同步方式直接返回 paymentIntent。
type StripeConfirmPaymentParams = {
  elements: StripeElements;
  confirmParams?: { return_url?: string };
  redirect?: 'if_required' | 'always';
};

interface StripeInstance {
  elements: (options?: { clientSecret?: string; appearance?: any; locale?: string }) => StripeElements;
  // 拆分卡字段（cardNumber/cardExpiry/cardCvc）放进我们自己的布局；卡号经隐藏 iframe，
  // 不进我们 DOM/服务器（PCI SAQ A）。用 confirmCardPayment 提交，3DS 由 Stripe 自动处理。
  // payment_method 可为拆分卡 Element（手动输卡）或钱包返回的 pm id（字符串）。
  confirmCardPayment: (
    clientSecret: string,
    data?: { payment_method?: any },
    options?: { handleActions?: boolean }
  ) => Promise<StripeConfirmResult>;
  // Apple/Google Pay 钱包按钮：仅设备/浏览器支持且后台已启用时可用（canMakePayment 门控）。
  paymentRequest: (options: any) => StripePaymentRequest;
  // Payment Element（「更多支付方式」）确认：用 elements + return_url，redirect:'if_required'。
  confirmPayment: (params: StripeConfirmPaymentParams) => Promise<StripeConfirmResult>;
}

// 拆分卡字段（cardNumber/cardExpiry/cardCvc）的内部文本样式（贴合主题：主色 #2f6bff、Inter）。
// 注意：字段外框/圆角/聚焦环由我们自己的 CSS（.fs-field + Stripe 注入的 StripeElement--focus 类）控制，
// 这里只管 iframe 内部的文字/占位符颜色字号。
// Stripe style 在 elements.create 时固定，无法事后切换，所以做成「按当前主题返回 style」的函数；
// 主题变化时需用新 style 重挂卡字段（暗色用浅色文字、更暗的占位符）。
const cardElementStyle = (theme: CheckoutTheme) => ({
  base: {
    color: theme === 'dark' ? '#e5e7eb' : '#1a1f2e',
    fontFamily: "'Inter', -apple-system, 'PingFang SC', 'Microsoft YaHei', sans-serif",
    fontSize: '15px',
    fontSmoothing: 'antialiased',
    '::placeholder': { color: theme === 'dark' ? '#6b7280' : '#9ca3af' },
  },
  invalid: { color: '#ef4444', iconColor: '#ef4444' },
});

// 自有渠道（provider !== 'stripe'）：是否需要我们单独列为额外选项
const isOwnChannel = (m: CheckoutMethodOption): boolean => m.provider !== 'stripe';

// 单层方式列表：信用卡行用这个固定 key（Stripe 卡，拆分字段）；自有渠道用各自的 m.method。
// 同一时刻 selectedPayKey 只对应一个支付区，实现互斥展开。
const CARD_PAY_KEY = 'card';

// 「更多支付方式」行的固定 key：选中后懒加载一个排除了 card/link/cashapp 的 PaymentIntent，
// 用 Stripe Payment Element 展示其余已启用方式（Klarna/Affirm/ACH/SEPA/iDEAL 等）。
const MORE_PAY_KEY = '__more__';

// Payment Element 的 appearance：跟随明暗主题（白标，主色 #2f6bff / 暗色 #5b8bff，无紫色）。
const moreElementAppearance = (theme: CheckoutTheme) =>
  theme === 'dark'
    ? {
        theme: 'night' as const,
        variables: {
          colorPrimary: '#5b8bff',
          colorBackground: '#161b29',
          colorText: '#e5e7eb',
          borderRadius: '10px',
          fontFamily: "'Inter', -apple-system, 'PingFang SC', 'Microsoft YaHei', sans-serif",
        },
      }
    : {
        theme: 'stripe' as const,
        variables: {
          colorPrimary: '#2f6bff',
          colorBackground: '#ffffff',
          colorText: '#1a1f2e',
          borderRadius: '10px',
          fontFamily: "'Inter', -apple-system, 'PingFang SC', 'Microsoft YaHei', sans-serif",
        },
      };

// 支付方式视觉：每种方式一个可识别的 FontAwesome 品牌图标 + 品牌色（白标、无 emoji）。
// 用 FA 官方品牌图标比手画 SVG 更标准、统一；无 FA 品牌图标的方式用 solid 通用图标兜底。
interface MethodFaVisual {
  icon: IconDefinition; // FontAwesome 图标定义
  color: string; // 品牌色（图标着色 + 选中描边）
}

// 渲染一枚 FA 图标到现有 .fs-pm-brand 28px 容器内（图标本身约 18px，居中对齐）。
const renderFa = (icon: IconDefinition, color: string): React.ReactNode => (
  <FontAwesomeIcon icon={icon} style={{ color, fontSize: 22 }} aria-hidden="true" />
);

// 统一映射：根据方式标识 / 渠道 / 集成类型，返回 {icon, color}。
// 自有渠道（provider !== 'stripe'，如 epay/crypto）与 Stripe 方式都走这里，保证图标风格一致。
const methodFaIcon = (
  method?: string,
  provider?: string,
  integrationType?: string,
  label?: string,
): MethodFaVisual => {
  const k = (method || '').toLowerCase();
  const lbl = (label || '').toLowerCase();
  const hit = (re: RegExp) => re.test(k) || re.test(lbl);

  // —— 自有渠道（中国本地支付 + 加密货币）——
  if (hit(/wechat|weixin|wx|微信/)) return { icon: faWeixin, color: '#07c160' };
  if (hit(/alipay|ali_pay|zhifubao|支付宝/)) return { icon: faAlipay, color: '#1677ff' };
  if (hit(/\bqq\b/)) return { icon: faQq, color: '#12b7f5' };
  // 银联无专属 FA 品牌图标：用中性的 credit-card 兜底（银联红 #e21836 着色保留品牌感）。
  if (hit(/union|unionpay|银联/)) return { icon: faCreditCard, color: '#e21836' };
  if (integrationType === 'crypto' || hit(/usdt|usdc|crypto|btc|eth|trc|erc|加密|数字货币/))
    return { icon: faBitcoin, color: '#f7931a' };

  // —— Stripe 钱包 / 品牌方式 ——
  if (hit(/apple[\s_-]?pay/)) return { icon: faApplePay, color: '#000000' };
  if (hit(/amazon[\s_-]?pay/)) return { icon: faAmazonPay, color: '#ff9900' };
  if (hit(/paypal/)) return { icon: faPaypal, color: '#003087' };

  // —— 银行 / 转账类（ACH/SEPA/网银）——
  if (hit(/bank|sepa|ach|transfer|转账|网银/)) return { icon: faBuildingColumns, color: '#2f6bff' };

  // —— 无 FA 品牌图标的方式：solid 通用图标兜底 ——
  if (hit(/upi|pix/)) return { icon: faMoneyBill, color: '#2f6bff' };
  if (hit(/klarna|affirm|afterpay|分期/)) return { icon: faMoneyBill, color: '#2f6bff' };

  // —— 集成类型兜底 ——
  if (integrationType === 'qr') return { icon: faQrcode, color: '#2f6bff' };
  if (integrationType === 'redirect') return { icon: faCreditCard, color: '#6b7280' };

  // —— 默认：银行卡（card / 其它）。Stripe 组用更中性的 credit-card；可视情况换 faCcVisa。——
  if (provider === 'stripe' || hit(/card|银行卡|信用卡|visa|master/)) {
    // 卡组：用 Visa 品牌图标更直观可辨（也可换回 faCreditCard 走中性风）。
    if (hit(/visa/)) return { icon: faCcVisa, color: '#1a1f71' };
    return { icon: faCreditCard, color: '#2f6bff' };
  }
  return { icon: faCreditCard, color: '#2f6bff' };
};

// 自有渠道（provider !== 'stripe'）：转交统一映射。
const ownChannelVisual = (m: CheckoutMethodOption): MethodFaVisual =>
  methodFaIcon(m.method, m.provider, m.integration_type, m.label);

// prepare 失败文案：会话过期/已完成时给出明确指引，其余按真失败处理。
// 仅判断展示文案，不改任何支付/互斥/effect 逻辑。
const prepareErrorText = (error: any, session: PublicCheckoutSession | null): string => {
  const status = session?.status;
  const httpStatus = error?.response?.status;
  // 后端把详情放在 data.error，概述放 data.message，两者都纳入判断。
  const serverMsg: string = `${error?.response?.data?.message || ''} ${error?.response?.data?.error || ''}`;
  const expiredOrDone =
    status === 'expired' ||
    status === 'complete' ||
    status === 'canceled' ||
    httpStatus === 410 ||
    /过期|已完成|已支付|已取消|expired|complete|canceled|already\s*paid/i.test(serverMsg);
  if (expiredOrDone) {
    return '该支付链接已过期或已完成，请向商家重新获取。';
  }
  // 会话不存在 / 已失效 / 非 open：多为打开了旧链接（支付链接一次性、有时效）。
  const goneOrInvalid =
    httpStatus === 404 ||
    /not\s*found|不存在|失效|is not open|未找到/i.test(serverMsg);
  if (goneOrInvalid) {
    return '该支付链接已失效或不存在，请向商家重新获取（每个支付链接仅在有效期内可用）。';
  }
  return '初始化失败，请刷新重试。';
};

// 动态加载 Stripe.js（单例），不引入 npm 依赖。返回构造函数（用全局 window.Stripe 类型）。
let stripeJsPromise: Promise<typeof window.Stripe | undefined> | null = null;
const loadStripeJs = (): Promise<typeof window.Stripe | undefined> => {
  if (typeof window !== 'undefined' && window.Stripe) {
    return Promise.resolve(window.Stripe);
  }
  if (stripeJsPromise) return stripeJsPromise;
  stripeJsPromise = new Promise<typeof window.Stripe | undefined>((resolve) => {
    if (typeof document === 'undefined') {
      resolve(undefined);
      return;
    }
    const existing = document.querySelector<HTMLScriptElement>('script[data-fs-stripe="1"]');
    if (existing) {
      existing.addEventListener('load', () => resolve(window.Stripe));
      existing.addEventListener('error', () => resolve(undefined));
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.async = true;
    script.dataset.fsStripe = '1';
    script.onload = () => resolve(window.Stripe);
    script.onerror = () => resolve(undefined);
    document.head.appendChild(script);
  });
  return stripeJsPromise;
};

interface PaymentMethodOption {
  type: string;
  label: string;
  icon: React.ReactNode;
}

const PAYMENT_METHODS: PaymentMethodOption[] = [
  { type: 'card', label: '银行卡', icon: <CreditCardOutlined /> },
  { type: 'alipay', label: '支付宝', icon: <span style={{ color: '#1677FF', fontWeight: 600 }}>支</span> },
  { type: 'wechat', label: '微信支付', icon: <span style={{ color: '#07C160', fontWeight: 600 }}>微</span> },
];

const formatMoney = (amount: number, currency: string = 'USD') => {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format((amount || 0) / 100);
  } catch {
    return `${((amount || 0) / 100).toFixed(2)} ${currency || 'USD'}`;
  }
};

// 左栏订单摘要下方的「安全保障 / 信任」区：克制灰度小字，平衡单件订单时的空白。
// 受理方式图标复用项目已接的 FontAwesome 品牌图标（灰度展示，表示"我们受理这些"）。
const ACCEPTED_BRANDS: { icon: IconDefinition; label: string }[] = [
  { icon: faCcVisa, label: 'Visa' },
  { icon: faCcMastercard, label: 'Mastercard' },
  { icon: faCcAmex, label: 'American Express' },
  { icon: faApplePay, label: 'Apple Pay' },
  { icon: faGooglePay, label: 'Google Pay' },
];

// 信任区。lang 可选：token 收银会话流传入当前语言走 i18n；旧 payment-intent 流不传，回退中文。
// 注意：原「由 FastStar 代商家安全收款，支持退款与争议保护。」一行已按需求删除。
const CheckoutTrust: React.FC<{ lang?: CheckoutLang }> = ({ lang }) => {
  const L: CheckoutLang = lang || 'zh';
  return (
    <div className="fs-trust">
      <div className="fs-trust-line">
        <LockOutlined className="fs-trust-ico" aria-hidden="true" />
        <span>{t('trust_encryption', L)}</span>
      </div>
      <div className="fs-trust-brands" aria-label={t('trust_brands_aria', L)}>
        {ACCEPTED_BRANDS.map((b) => (
          <FontAwesomeIcon
            key={b.label}
            icon={b.icon}
            className="fs-trust-brand"
            title={b.label}
            aria-label={b.label}
          />
        ))}
      </div>
      <div className="fs-trust-help">{t('trust_help', L)}</div>
    </div>
  );
};

// ---- 新会话流（?token=）：渲染脱敏的收银会话 ----

// mode → i18n key（摘要副标题用）。
const MODE_SUMMARY_KEY: Record<string, Parameters<typeof t>[0]> = {
  payment: 'mode_payment',
  subscription: 'mode_subscription',
  setup: 'mode_setup',
};

// 会话状态 → {标题 key, 副标题 key}。运行时按当前语言 t() 出来。
const SESSION_STATUS_KEY: Record<
  string,
  { title: Parameters<typeof t>[0]; subtitle: Parameters<typeof t>[0] }
> = {
  complete: { title: 'session_complete_title', subtitle: 'session_complete_subtitle' },
  processing: { title: 'session_processing_title', subtitle: 'session_processing_subtitle' },
  expired: { title: 'session_expired_title', subtitle: 'session_expired_subtitle' },
  canceled: { title: 'session_canceled_title', subtitle: 'session_canceled_subtitle' },
};

// Stripe 跳转返回（3DS / 重定向支付）后 return_url 上携带的结果参数。
// 一旦检测到 redirect_status，就用结果态展示，跳过 prepare / 不挂 Element，
// 避免对一个已 processing/complete 或 webhook 未及时置态的会话重复初始化而误报"初始化失败"。
type RedirectResultKind = 'success' | 'processing' | 'failed';

// 解析 URL 上的 Stripe 跳转返回参数。无 redirect_status 时返回 null（走正常初始化流程）。
const parseRedirectResult = (params: URLSearchParams): RedirectResultKind | null => {
  const status = (params.get('redirect_status') || '').toLowerCase();
  if (!status) return null;
  if (status === 'succeeded') return 'success';
  if (status === 'processing') return 'processing';
  return 'failed';
};

const CheckoutSessionFlow: React.FC<{ token: string }> = ({ token }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Stripe 跳转返回结果（仅在挂载时根据 URL 计算一次）：非 null 时直接进结果态，gate 住 prepare 与 Element。
  const [redirectResult] = useState<RedirectResultKind | null>(() =>
    parseRedirectResult(searchParams)
  );

  // —— 语言 & 主题：在收银会话流统一管理，向下传给外壳切换器与卡字段挂载逻辑 ——
  // 语言优先级：URL ?lang= > localStorage > navigator.language > 默认 en。
  const [lang, setLang] = useState<CheckoutLang>(() => detectLang(searchParams.toString()));
  // 主题：手动(localStorage) > 系统 prefers-color-scheme > 亮色。
  const [theme, setTheme] = useState<CheckoutTheme>(() => detectTheme());
  // 翻译快捷封装：固定当前 lang。
  const tr = useCallback(
    (key: Parameters<typeof t>[0], vars?: Record<string, string | number>) => t(key, lang, vars),
    [lang]
  );
  // 切换语言：存 localStorage，即时生效（卡字段重挂由下方依赖 lang 的 effect 触发）。
  const handleLangChange = useCallback((next: CheckoutLang) => {
    setLang(next);
    try {
      window.localStorage.setItem(LANG_STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);
  // 切换明暗：手动优先，存 localStorage（卡字段重挂同样由依赖 theme 的 effect 触发）。
  const handleThemeToggle = useCallback(() => {
    setTheme((prev) => {
      const next: CheckoutTheme = prev === 'dark' ? 'light' : 'dark';
      try {
        window.localStorage.setItem(THEME_STORAGE_KEY, next);
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);
  const [session, setSession] = useState<PublicCheckoutSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  // 'idle' | 'requires_action' | 'success'
  const [confirmState, setConfirmState] = useState<'idle' | 'requires_action' | 'success'>('idle');

  // 收卡状态：payment 模式（单层方式列表，每个方式一行，互斥展开）
  // - 信用卡 → 我们自己布局的拆分卡字段（cardNumber/cardExpiry/cardCvc），下面是「支付 {金额}」按钮 → confirmCardPayment
  // - Apple/Google Pay → 列表上方钱包按钮（canMakePayment 门控）
  // - 自有渠道（provider !== 'stripe'，epay/crypto）→ 各自一行，点击发起 pay-own-channel 并按集成类型渲染
  const [payError, setPayError] = useState<string | null>(null);
  const [cardError, setCardError] = useState<string | null>(null); // 卡字段实时校验错误（卡号/有效期/CVC）
  const [elementReady, setElementReady] = useState(false); // 拆分卡字段是否 ready（gate 支付按钮）
  const stripeRef = useRef<StripeInstance | null>(null);
  const elementsRef = useRef<StripeElements | null>(null); // 拆分卡字段的 elements
  const cardNumberElRef = useRef<StripeElement | null>(null); // 卡号字段（confirmCardPayment 传它）
  const cardExpiryElRef = useRef<StripeElement | null>(null);
  const cardCvcElRef = useRef<StripeElement | null>(null);
  const paymentIntentIdRef = useRef<string>('');
  const prepStartedRef = useRef<boolean>(false); // 防止 prepare 重复发起（见下方 effect 注释）
  const elementMountedRef = useRef(false); // 拆分卡字段已挂载

  // Apple/Google Pay 钱包按钮：仅设备/浏览器支持且后台启用时出现（canMakePayment 门控）。
  const [walletAvailable, setWalletAvailable] = useState(false);
  const walletMountedRef = useRef(false);

  // 统一收银台：方式清单（拆分为 Stripe 卡与自有渠道）
  const [methods, setMethods] = useState<CheckoutMethodOption[]>([]);
  const [prepLoading, setPrepLoading] = useState(false);
  const stripeCtxRef = useRef<{ publishableKey: string; clientSecret: string; country: string; stripeAccount?: string } | null>(null);

  // 统一单选：当前选中的支付方式 key。CARD_PAY_KEY('card') 表示展开拆分卡字段区；
  // 其它值表示展开对应自有渠道。同一时刻只展开一个支付区（互斥）。
  const [selectedPayKey, setSelectedPayKey] = useState<string>('');
  const [ownLoading, setOwnLoading] = useState(false);
  const [ownError, setOwnError] = useState<string | null>(null);
  const [ownResults, setOwnResults] = useState<Record<string, OwnChannelResult>>({});
  const [copied, setCopied] = useState(false);

  // 「更多支付方式」（Payment Element）：独立于卡字段的 ctx/loading/error/ready/mounted，
  // 绝不复用 stripeCtxRef / prepStartedRef，避免与拆分卡字段相互干扰。
  const [moreLoading, setMoreLoading] = useState(false);
  const [moreError, setMoreError] = useState<string | null>(null);
  const [moreReady, setMoreReady] = useState(false); // Payment Element 'ready' → gate 确认按钮
  const moreCtxRef = useRef<{ publishableKey: string; clientSecret: string; country: string; stripeAccount?: string } | null>(null);
  const moreStartedRef = useRef(false); // 防止 prepare-element 重复发起
  const moreElementsRef = useRef<StripeElements | null>(null);
  const moreElementRef = useRef<StripeElement | null>(null);
  const moreMountedRef = useRef(false);
  // Payment Element 重挂用：记录上次挂载时的 lang/theme（locale/appearance 在 elements() 时固定，切换后需重挂）。
  const moreLocaleStyleRef = useRef<{ lang: CheckoutLang; theme: CheckoutTheme }>({ lang, theme });

  // 是否存在可走 Stripe 的方式 → 渲染一条"信用卡"行（拆分字段）。
  // 钱包（Apple/Google Pay）不作为手动行，而是在列表上方按 canMakePayment 自动出现。
  const hasCardMethod = methods.some((m) => m.provider === 'stripe');
  const ownMethods = methods.filter(isOwnChannel);
  // 选中的是否是信用卡行（拆分卡字段区）
  const cardSelected = selectedPayKey === CARD_PAY_KEY;
  // 选中的是否是「更多支付方式」行（Payment Element 区）
  const moreSelected = selectedPayKey === MORE_PAY_KEY;
  // 是否存在「非卡非钱包的 Stripe 方式」→ 才显示「更多支付方式」入口。
  const hasMoreMethods = methods.some(
    (m) =>
      m.provider === 'stripe' &&
      !['card', 'apple_pay', 'google_pay', 'link', 'cashapp'].includes(m.method)
  );
  // 选中的自有渠道（非信用卡、非更多方式时）
  const activeOwnMethod = !cardSelected && !moreSelected
    ? ownMethods.find((m) => m.method === selectedPayKey) || null
    : null;
  // 自有渠道的有效集成类型：优先用运行时 pay-own-channel 返回的（epay mapi 动态决定 qr/jump），
  // 回退到方式目录里的默认值。
  const activeOwnType = activeOwnMethod
    ? ownResults[activeOwnMethod.method]?.integration_type || activeOwnMethod.integration_type
    : null;

  const embedded = isEmbedded();

  // 统一传给每个 CheckoutShell 的语言/主题 props（切换器 + data-theme）。
  const shellProps = {
    hideBrand: embedded,
    lang,
    onLangChange: handleLangChange,
    theme,
    onThemeToggle: handleThemeToggle,
  };

  const loadSession = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await CatalogService.getPublicCheckoutSession(token);
      setSession(data);
    } catch (error: any) {
      setLoadError(error.response?.data?.message || tr('load_session_failed'));
    } finally {
      setLoading(false);
    }
  }, [token, tr]);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  // Stripe 跳转返回成功：仅桥接通知宿主一次（embedded 时由宿主处理），用 ref 记录是否被宿主接管。
  const redirectNotifiedRef = useRef(false);
  const redirectSuccessHandledRef = useRef(false);
  useEffect(() => {
    if (redirectResult !== 'success' || redirectNotifiedRef.current) return;
    redirectNotifiedRef.current = true;
    redirectSuccessHandledRef.current = notifyCheckoutResult('success', {
      checkout_session_id: session?.id,
      payment_intent_id: searchParams.get('payment_intent') || undefined,
    });
  }, [redirectResult, session, searchParams]);

  // payment 模式：调 prepare 拿方式清单 + Stripe 上下文（此时不挂 Element，待选中 stripe 方式再挂）
  // 注意：依赖数组只放 [session, token]。不要把 prepLoading/methods.length 放进依赖——
  // 否则 setPrepLoading(true) 会触发 effect 重跑，cleanup 把首次请求 cancelled 置 true，
  // 导致 prepare 返回后 setMethods/setPrepLoading(false) 被跳过，UI 永远卡在"加载支付方式"。
  // 用 prepStartedRef 守卫防重复发起。
  useEffect(() => {
    // Stripe 跳转返回（带 redirect_status）：已在跳转支付侧完成确认，直接进结果态，绝不再 prepare。
    if (redirectResult) return;
    if (!session || session.status !== 'open' || session.mode !== 'payment') return;
    if (prepStartedRef.current || paymentIntentIdRef.current) return;
    prepStartedRef.current = true;

    let cancelled = false;
    (async () => {
      try {
        setPrepLoading(true);
        setPayError(null);
        const prep = await CatalogService.preparePublicCheckoutPayment(token);
        if (cancelled) return;
        paymentIntentIdRef.current = prep.payment_intent_id;
        stripeCtxRef.current = {
          publishableKey: prep.publishable_key,
          clientSecret: prep.client_secret,
          country: (prep.country || 'US').toUpperCase(),
          stripeAccount: prep.stripe_account || undefined,
        };

        // 后端未返回 methods 时，回退为单一 stripe 卡方式（兼容旧契约）。
        let list = prep.methods && prep.methods.length > 0 ? prep.methods : [];
        if (list.length === 0 && prep.client_secret && prep.publishable_key) {
          list = [
            {
              method: 'card',
              label: '信用卡 / 借记卡',
              provider: 'stripe',
              integration_type: 'stripe_element',
            },
          ];
        }
        setMethods(list);
        // 默认选中：有 Stripe 方式则选中"信用卡"行（拆分字段），否则选中第一个自有渠道。
        const hasStripe = list.some((m) => m.provider === 'stripe');
        const firstOwn = list.find((m) => m.provider !== 'stripe');
        setSelectedPayKey(hasStripe ? CARD_PAY_KEY : firstOwn ? firstOwn.method : '');
      } catch (error: any) {
        if (cancelled) return;
        prepStartedRef.current = false; // 允许刷新/重试
        setPayError(prepareErrorText(error, session));
      } finally {
        if (!cancelled) setPrepLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [session, token, redirectResult]);

  // 取得（单例）Stripe 实例。publishable key 优先用卡的 ctx，回退「更多方式」的 ctx
  //（两者 publishable key 相同，单例只构造一次）。
  const ensureStripe = useCallback(async (): Promise<StripeInstance | null> => {
    if (stripeRef.current) return stripeRef.current;
    const key = stripeCtxRef.current?.publishableKey || moreCtxRef.current?.publishableKey;
    if (!key) return null;
    const Stripe = await loadStripeJs();
    if (!Stripe) return null;
    // PSP/Connect 模式：direct charge 的 PI 在商户连接账户上，Stripe.js 必须带 stripeAccount 初始化，
    // 否则 confirmCardPayment / Payment Element / 钱包 都会因找不到该 PI 而失败。MoR 模式为空，正常初始化。
    const acct = stripeCtxRef.current?.stripeAccount || moreCtxRef.current?.stripeAccount;
    const stripe = (
      acct ? (Stripe as any)(key, { stripeAccount: acct }) : Stripe(key)
    ) as unknown as StripeInstance;
    stripeRef.current = stripe;
    return stripe;
  }, []);

  // 选中"信用卡"行时：把拆分卡字段（cardNumber/cardExpiry/cardCvc）挂进我们自己的布局。
  // 这些 Element 用 elements()（不带 clientSecret）创建，卡号经隐藏 iframe，不进我们 DOM/服务器（PCI SAQ A）。
  // 外框/圆角/聚焦环由我们的 CSS 控制；时序稳健：三个容器渲染后再 mount，cardNumber 的 'ready' gate 支付按钮。
  useEffect(() => {
    if (!hasCardMethod) return;
    if (!cardSelected) return;
    if (elementMountedRef.current) return;
    const ctx = stripeCtxRef.current;
    if (!ctx || !ctx.clientSecret) return;

    let cancelled = false;
    let raf = 0;
    const tryMount = async () => {
      try {
        setPayError(null);
        const stripe = await ensureStripe();
        if (cancelled) return;
        if (!stripe) {
          setPayError(tr('component_load_failed'));
          return;
        }
        const cn = document.getElementById('fs-card-number');
        const ce = document.getElementById('fs-card-expiry');
        const cc = document.getElementById('fs-card-cvc');
        if (!cn || !ce || !cc) {
          // 容器尚未渲染：下一帧重试
          raf = window.requestAnimationFrame(() => {
            void tryMount();
          });
          return;
        }
        if (elementMountedRef.current || cancelled) return;
        // locale 跟随当前语言、style 跟随当前主题（两者在 create 时固定，切换后靠重挂应用）。
        const cardStyle = cardElementStyle(theme);
        const elements = stripe.elements({ locale: stripeLocale(lang) });
        const cardNumber = elements.create('cardNumber', {
          style: cardStyle,
          showIcon: true,
          placeholder: '1234 5678 9012 3456',
        });
        const cardExpiry = elements.create('cardExpiry', { style: cardStyle });
        const cardCvc = elements.create('cardCvc', { style: cardStyle });

        const onChange = (ev: any) => {
          if (!cancelled) setCardError(ev?.error?.message || null);
        };
        cardNumber.on('ready', () => {
          if (!cancelled) setElementReady(true);
        });
        cardNumber.on('change', onChange);
        cardExpiry.on('change', onChange);
        cardCvc.on('change', onChange);

        cardNumber.mount(cn);
        cardExpiry.mount(ce);
        cardCvc.mount(cc);

        elementsRef.current = elements;
        cardNumberElRef.current = cardNumber;
        cardExpiryElRef.current = cardExpiry;
        cardCvcElRef.current = cardCvc;
        elementMountedRef.current = true;
      } catch (error: any) {
        if (cancelled) return;
        setPayError(error.response?.data?.message || tr('init_pay_failed'));
      }
    };
    void tryMount();

    return () => {
      cancelled = true;
      if (raf) window.cancelAnimationFrame(raf);
    };
    // lang/theme 进入依赖：切换后配合下方「销毁重挂」effect，让卡字段以新 locale/style 重挂。
  }, [hasCardMethod, cardSelected, ensureStripe, lang, theme, tr]);

  // 互斥切换：离开信用卡行（选了自有渠道）时销毁已挂载的卡字段，容器卸载后能干净重挂。
  useEffect(() => {
    if (cardSelected) return;
    if (!elementMountedRef.current) return;
    [cardNumberElRef, cardExpiryElRef, cardCvcElRef].forEach((r) => {
      const el = r.current;
      if (el) {
        try {
          if (el.destroy) el.destroy();
          else el.unmount();
        } catch {
          /* 忽略销毁异常 */
        }
      }
      r.current = null;
    });
    elementsRef.current = null;
    elementMountedRef.current = false;
    setElementReady(false);
    setCardError(null);
  }, [cardSelected]);

  // 语言 / 主题切换时：若卡字段已挂载，销毁它（清掉 elementMountedRef / ready gate）。
  // Stripe 的 locale 与 style 在 create 时固定，切换后必须重挂才能生效；销毁后上面的挂载 effect
  // （依赖含 lang/theme）会自动重挂并以新 locale/style 重建，重挂后 cardNumber 'ready' 再 enable 支付按钮。
  // 用 ref 记录上次的 lang/theme，避免首次挂载时误销毁。
  const lastLocaleStyleRef = useRef<{ lang: CheckoutLang; theme: CheckoutTheme }>({ lang, theme });
  useEffect(() => {
    const prev = lastLocaleStyleRef.current;
    if (prev.lang === lang && prev.theme === theme) return; // 无变化（含首挂）
    lastLocaleStyleRef.current = { lang, theme };
    if (!elementMountedRef.current) return; // 卡字段未挂载（未选信用卡）则无需处理
    [cardNumberElRef, cardExpiryElRef, cardCvcElRef].forEach((r) => {
      const el = r.current;
      if (el) {
        try {
          if (el.destroy) el.destroy();
          else el.unmount();
        } catch {
          /* 忽略销毁异常 */
        }
      }
      r.current = null;
    });
    elementsRef.current = null;
    elementMountedRef.current = false;
    setElementReady(false);
    setCardError(null);
  }, [lang, theme]);

  // 展开某个自有渠道（qr/redirect/crypto）时发起 pay-own-channel；按 method 缓存，redirect 直接跳转。
  useEffect(() => {
    if (!activeOwnMethod) return;
    if (ownResults[activeOwnMethod.method]) return; // 已发起过

    let cancelled = false;
    (async () => {
      try {
        setOwnLoading(true);
        setOwnError(null);
        const res = await CatalogService.payOwnChannel(token, activeOwnMethod.method);
        if (cancelled) return;
        setOwnResults((prev) => ({ ...prev, [activeOwnMethod.method]: res }));
        // 跳转类（含加密货币托管页）不再自动跳转：展示一个明确的"前往支付"确认按钮，
        // 让买家自己点击再跳走（取消/返回后也能再点），避免一选中就被甩到外部页面。
      } catch (error: any) {
        if (cancelled) return;
        const status = error.response?.status;
        if (status === 501) {
          setOwnError(tr('own_method_unavailable'));
        } else {
          setOwnError(error.response?.data?.message || tr('own_initiate_failed'));
        }
      } finally {
        if (!cancelled) setOwnLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPayKey]);

  // 复制加密货币地址
  const handleCopyAddress = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      message.warning(tr('msg_copy_failed'));
    }
  };

  // 统一的支付成功收尾：success 桥接 / 跳转。requires_action 时切到验证态。
  const finalizePaymentSuccess = useCallback(
    async (status?: string) => {
      if (!session) return;
      if (status === 'requires_action') {
        setConfirmState('requires_action');
        return;
      }
      message.success(tr('msg_pay_success'));
      setConfirmState('success');
      const handled = notifyCheckoutResult('success', {
        checkout_session_id: session.id,
        payment_intent_id: paymentIntentIdRef.current,
      });
      if (!handled) {
        navigate(`/checkout/success?session_id=${session.id}`);
      }
    },
    [session, navigate, tr]
  );

  // 信用卡：用拆分卡字段（cardNumber Element）+ client_secret 调 confirmCardPayment。
  // 3DS 由 Stripe 以弹窗 / 必要时重定向自动处理；成功后走统一收尾。
  const handlePayWithCard = async () => {
    if (!session) return;
    const stripe = stripeRef.current;
    const cardEl = cardNumberElRef.current;
    const ctx = stripeCtxRef.current;
    if (!stripe || !cardEl) {
      message.error(tr('msg_component_not_ready'));
      return;
    }
    if (!ctx || !ctx.clientSecret) {
      message.error(tr('msg_not_initialized'));
      return;
    }
    setProcessing(true);
    setPayError(null);
    try {
      // 会话带客户邮箱时兜底填入账单邮箱
      const billing_details: { email?: string } = {};
      if (session.customer_email) billing_details.email = session.customer_email;
      const { error, paymentIntent } = await stripe.confirmCardPayment(ctx.clientSecret, {
        payment_method: { card: cardEl, billing_details },
      });

      if (error) {
        const msg = error.message || tr('msg_card_failed');
        setPayError(msg);
        notifyCheckoutResult('error', { checkout_session_id: session.id, message: msg });
        return;
      }
      await finalizePaymentSuccess(paymentIntent?.status);
    } catch (error: any) {
      const msg = error?.message || tr('msg_pay_failed');
      setPayError(msg);
      notifyCheckoutResult('error', { checkout_session_id: session.id, message: msg });
    } finally {
      setProcessing(false);
    }
  };

  // Apple/Google Pay 钱包按钮：有 Stripe 卡方式时尝试初始化。
  // canMakePayment 为空（设备/浏览器不支持，或后台未启用钱包 / 未注册域名）→ 不显示按钮，零副作用。
  // 钱包确认走 paymentmethod 事件：拿 pm id → confirmCardPayment（handleActions:false）→ 必要时再确认一次（3DS）。
  useEffect(() => {
    if (!hasCardMethod || walletMountedRef.current) return;
    if (!session || session.mode !== 'payment') return;
    const ctx = stripeCtxRef.current;
    if (!ctx || !ctx.clientSecret) return;

    let cancelled = false;
    (async () => {
      try {
        const stripe = await ensureStripe();
        if (cancelled || !stripe || !stripe.paymentRequest) return;
        const pr = stripe.paymentRequest({
          country: ctx.country || 'US',
          currency: (session.currency || 'usd').toLowerCase(),
          total: { label: session.merchant_name || '订单', amount: session.amount_total || 0 },
          requestPayerName: false,
          requestPayerEmail: false,
        });
        const result = await pr.canMakePayment();
        if (cancelled || !result) return; // 不支持 → 不显示钱包按钮
        const container = document.getElementById('fs-wallet-button');
        if (!container || cancelled || walletMountedRef.current) return;

        pr.on('paymentmethod', async (ev: any) => {
          try {
            const first = await stripe.confirmCardPayment(
              ctx.clientSecret,
              { payment_method: ev.paymentMethod.id },
              { handleActions: false }
            );
            if (first.error) {
              ev.complete('fail');
              setPayError(first.error.message || tr('msg_pay_failed'));
              return;
            }
            ev.complete('success');
            // requires_action（3DS 等）：再确认一次让 Stripe 弹出验证。
            if (first.paymentIntent && first.paymentIntent.status === 'requires_action') {
              const second = await stripe.confirmCardPayment(ctx.clientSecret);
              if (second.error) {
                setPayError(second.error.message || tr('msg_pay_failed'));
                notifyCheckoutResult('error', {
                  checkout_session_id: session.id,
                  message: second.error.message || tr('msg_pay_failed'),
                });
                return;
              }
              await finalizePaymentSuccess(second.paymentIntent?.status);
            } else {
              await finalizePaymentSuccess(first.paymentIntent?.status);
            }
          } catch (e: any) {
            ev.complete('fail');
            setPayError(e?.message || tr('msg_pay_failed'));
          }
        });

        const elements = stripe.elements({ locale: stripeLocale(lang) });
        const prButton = elements.create('paymentRequestButton', {
          paymentRequest: pr,
          // 钱包按钮主题跟随明暗（暗色用 light 按钮更醒目，亮色用 dark）。
          style: {
            paymentRequestButton: {
              type: 'default',
              theme: theme === 'dark' ? 'light' : 'dark',
              height: '48px',
            },
          },
        });
        prButton.mount(container);
        walletMountedRef.current = true;
        if (!cancelled) setWalletAvailable(true);
      } catch {
        /* 钱包初始化失败：静默降级，不影响卡 / 自有渠道 */
      }
    })();

    return () => {
      cancelled = true;
    };
    // 钱包按钮仅挂载一次（walletMountedRef 守卫）；lang/theme 入依赖只为消除 lint 提示，
    // 守卫保证不会因切换而重挂（钱包是次级入口，locale/theme 以首挂为准）。
  }, [hasCardMethod, ensureStripe, session, finalizePaymentSuccess, lang, theme, tr]);

  // —— 「更多支付方式」：选中 __more__ 时懒加载 prepare-element + 挂载 Payment Element ——
  // 用独立的 moreCtxRef/moreStartedRef/moreMountedRef，绝不动卡字段那套。
  useEffect(() => {
    if (!moreSelected) return;
    if (moreMountedRef.current) return;
    if (!session || session.mode !== 'payment') return;

    let cancelled = false;
    let raf = 0;
    (async () => {
      try {
        setMoreError(null);
        // 1) 懒加载拿 client_secret（排除 card/link/cashapp 的 PI）。仅首次发起一次。
        if (!moreCtxRef.current) {
          if (moreStartedRef.current) return;
          moreStartedRef.current = true;
          setMoreLoading(true);
          const prep = await CatalogService.preparePublicCheckoutElement(token);
          if (cancelled) return;
          moreCtxRef.current = {
            publishableKey: prep.publishable_key,
            clientSecret: prep.client_secret,
            country: (prep.country || 'US').toUpperCase(),
            stripeAccount: prep.stripe_account || undefined,
          };
        }
        const ctx = moreCtxRef.current;
        if (!ctx || !ctx.clientSecret) {
          setMoreError(tr('init_pay_failed'));
          return;
        }
        // 2) 取 Stripe 实例（单例，与卡同 key）。
        const stripe = await ensureStripe();
        if (cancelled) return;
        if (!stripe) {
          setMoreError(tr('component_load_failed'));
          return;
        }
        // 3) 容器渲染后再挂（下一帧重试时序稳健）。
        const tryMount = () => {
          if (cancelled || moreMountedRef.current) return;
          const container = document.getElementById('fs-more-element');
          if (!container) {
            raf = window.requestAnimationFrame(tryMount);
            return;
          }
          const elements = stripe.elements({
            clientSecret: ctx.clientSecret,
            appearance: moreElementAppearance(theme),
            locale: stripeLocale(lang),
          });
          const paymentEl = elements.create('payment', {
            layout: { type: 'accordion', defaultCollapsed: false },
          });
          paymentEl.on('ready', () => {
            if (!cancelled) setMoreReady(true);
          });
          paymentEl.mount(container);
          moreElementsRef.current = elements;
          moreElementRef.current = paymentEl;
          moreMountedRef.current = true;
          moreLocaleStyleRef.current = { lang, theme };
        };
        tryMount();
      } catch (error: any) {
        if (cancelled) return;
        moreStartedRef.current = false; // 允许重试
        setMoreError(prepareErrorText(error, session));
      } finally {
        if (!cancelled) setMoreLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (raf) window.cancelAnimationFrame(raf);
    };
    // lang/theme 进入依赖：配合下方销毁 effect，让 Payment Element 以新 locale/appearance 重挂。
  }, [moreSelected, session, token, ensureStripe, lang, theme, tr]);

  // 互斥切换：离开「更多支付方式」行时销毁 Payment Element（容器卸载后能干净重挂）。
  useEffect(() => {
    if (moreSelected) return;
    if (!moreMountedRef.current) return;
    const el = moreElementRef.current;
    if (el) {
      try {
        if (el.destroy) el.destroy();
        else el.unmount();
      } catch {
        /* 忽略销毁异常 */
      }
    }
    moreElementRef.current = null;
    moreElementsRef.current = null;
    moreMountedRef.current = false;
    setMoreReady(false);
  }, [moreSelected]);

  // 语言 / 主题切换时：若 Payment Element 已挂载，销毁它（locale/appearance 在 create 时固定，
  // 必须重挂才能生效）。销毁后上面依赖 lang/theme 的挂载 effect 会自动以新设置重建。
  useEffect(() => {
    const prev = moreLocaleStyleRef.current;
    if (prev.lang === lang && prev.theme === theme) return; // 无变化（含首挂）
    moreLocaleStyleRef.current = { lang, theme };
    if (!moreMountedRef.current) return;
    const el = moreElementRef.current;
    if (el) {
      try {
        if (el.destroy) el.destroy();
        else el.unmount();
      } catch {
        /* 忽略销毁异常 */
      }
    }
    moreElementRef.current = null;
    moreElementsRef.current = null;
    moreMountedRef.current = false;
    setMoreReady(false);
  }, [lang, theme]);

  // 「更多支付方式」确认：confirmPayment（elements + return_url），redirect:'if_required'。
  // 跳转/3DS 方式会在 Stripe 侧跳走，返回 return_url 后由现有 redirect_status 结果态处理；
  // 同步完成（无 error）则走统一成功收尾。
  const handlePayWithMore = async () => {
    if (!session) return;
    const stripe = stripeRef.current;
    const elements = moreElementsRef.current;
    if (!stripe || !elements || !stripe.confirmPayment) {
      message.error(tr('msg_component_not_ready'));
      return;
    }
    setProcessing(true);
    setMoreError(null);
    try {
      // return_url 用当前页 URL（保留 token 等参数），跳转方式返回后命中 redirect_status 结果态。
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required',
      });
      if (error) {
        const msg = error.message || tr('msg_pay_failed');
        setMoreError(msg);
        notifyCheckoutResult('error', { checkout_session_id: session.id, message: msg });
        return;
      }
      await finalizePaymentSuccess(paymentIntent?.status);
    } catch (error: any) {
      const msg = error?.message || tr('msg_pay_failed');
      setMoreError(msg);
      notifyCheckoutResult('error', { checkout_session_id: session.id, message: msg });
    } finally {
      setProcessing(false);
    }
  };

  // —— 异步支付状态轮询 —— 二维码（微信/支付宝扫码）、加密货币转账、以及「更多支付方式」发起后，
  // 买家在别处付款，靠 webhook 把会话置 complete。这里每 4 秒拉一次公开会话状态检测。
  // 触发：展示着自有渠道 qr/crypto 结果，或选中了 __more__ 且已发起 prepare-element。
  // 停止：组件卸载 / 已成功 / 切到非异步方式 / 会话 expired|canceled。卡（confirmCardPayment 同步）不轮询；
  // setup/subscription 模式不轮询。
  const pollIntervalRef = useRef<number | null>(null);
  useEffect(() => {
    // 非 payment 模式（setup/subscription）不轮询。
    if (!session || session.mode !== 'payment') return;
    // 已进入成功/验证态则不再轮询。
    if (confirmState !== 'idle') return;

    // 是否在展示异步自有渠道结果（qr / crypto）。
    const ownAsyncActive =
      !!activeOwnMethod &&
      !!ownResults[activeOwnMethod.method] &&
      (activeOwnType === 'qr' || activeOwnType === 'crypto');
    // 「更多支付方式」已发起（拿到 client_secret）。
    const moreActive = moreSelected && !!moreCtxRef.current;
    const shouldPoll = ownAsyncActive || moreActive;
    if (!shouldPoll) return;

    let stopped = false;
    const tick = async () => {
      try {
        const data = await CatalogService.getPublicCheckoutSession(token);
        if (stopped) return;
        if (data.status === 'complete') {
          // 成功：清轮询并走统一成功收尾（展示成功态 / 桥接通知）。
          if (pollIntervalRef.current) {
            window.clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          await finalizePaymentSuccess('succeeded');
        } else if (data.status === 'expired' || data.status === 'canceled') {
          // 终止态：停止轮询，更新会话以让 UI 进对应状态页。
          if (pollIntervalRef.current) {
            window.clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          setSession(data);
        }
        // processing / open：继续轮询，不打断。
      } catch {
        /* 请求失败静默重试下次 */
      }
    };

    // 用 ref 存 interval id，避免重复创建。
    pollIntervalRef.current = window.setInterval(tick, 4000);
    return () => {
      stopped = true;
      if (pollIntervalRef.current) {
        window.clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [
    session,
    token,
    confirmState,
    activeOwnMethod,
    activeOwnType,
    ownResults,
    moreSelected,
    finalizePaymentSuccess,
  ]);

  // setup / subscription 模式：保留原 confirm 流程
  const handleConfirm = async () => {
    if (!session) return;
    setProcessing(true);
    try {
      const result = await CatalogService.confirmPublicCheckoutSession(token);

      // 返回里带 client_secret / next_action 时，需要进一步验证
      const intent = result?.payment_intent || result?.setup_intent || {};
      const requiresAction = !!(
        intent.client_secret ||
        intent.next_action ||
        intent.status === 'requires_action'
      );
      if (requiresAction) {
        setConfirmState('requires_action');
        return;
      }

      message.success(session.mode === 'setup' ? tr('msg_setup_success') : tr('msg_pay_success'));
      setConfirmState('success');
      const handled = notifyCheckoutResult('success', {
        checkout_session_id: session.id,
        payment_intent_id: result?.payment_intent?.id,
        subscription_id: result?.subscription?.id,
      });
      if (!handled) {
        navigate(`/checkout/success?session_id=${session.id}`);
      }
    } catch (error: any) {
      const msg = error.response?.data?.message || tr('msg_pay_failed');
      message.error(msg);
      notifyCheckoutResult('error', { checkout_session_id: session.id, message: msg });
    } finally {
      setProcessing(false);
    }
  };

  const handleCancel = () => {
    if (!session) return;
    const handled = notifyCheckoutResult('cancel', {
      checkout_session_id: session.id,
      reason: 'user_cancelled',
    });
    if (!handled) {
      navigate(`/checkout/cancel?session_id=${session.id}`);
    }
  };

  // ---- 加载骨架屏 ----
  if (loading) {
    return (
      <CheckoutShell {...shellProps}>
        <div className="fs-card">
          <div className="fs-summary">
            <div className="fs-skeleton" style={{ width: 90, height: 14, marginBottom: 14 }} />
            <div className="fs-skeleton" style={{ width: 180, height: 38, marginBottom: 24 }} />
            <div className="fs-skeleton" style={{ width: '100%', height: 56, marginBottom: 12 }} />
            <div className="fs-skeleton" style={{ width: '100%', height: 56 }} />
          </div>
          <div className="fs-pay">
            <div className="fs-skeleton" style={{ width: 120, height: 18, marginBottom: 22 }} />
            <div className="fs-skeleton" style={{ width: '100%', height: 52, marginBottom: 10 }} />
            <div className="fs-skeleton" style={{ width: '100%', height: 52, marginBottom: 24 }} />
            <div className="fs-skeleton" style={{ width: '100%', height: 44 }} />
          </div>
        </div>
      </CheckoutShell>
    );
  }

  // ---- Stripe 跳转返回结果态（3DS / 重定向支付返回，URL 带 redirect_status） ----
  // 优先级高于 prepare / 会话状态：此处只展示结果并引导，绝不展示"初始化失败"。
  if (redirectResult) {
    if (redirectResult === 'success') {
      // 成功结果态。桥接通知已在上方 effect 内触发一次；handled 表示是否被宿主接管。
      const handled = redirectSuccessHandledRef.current;
      return (
        <CheckoutShell {...shellProps} merchantName={session?.merchant_name}>
          <div className="fs-card fs-card--single">
            <div className="fs-result">
              <div className="fs-result-icon fs-result-icon--success">✓</div>
              <div className="fs-result-title">{tr('result_pay_success_title')}</div>
              <div className="fs-result-subtitle">{tr('result_pay_success_subtitle')}</div>
              {!handled && session && (
                <div className="fs-result-actions">
                  <Button
                    type="primary"
                    size="large"
                    block
                    onClick={() => navigate(`/checkout/success?session_id=${session.id}`)}
                  >
                    {tr('result_view_detail')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CheckoutShell>
      );
    }

    if (redirectResult === 'processing') {
      return (
        <CheckoutShell {...shellProps} merchantName={session?.merchant_name}>
          <div className="fs-card fs-card--single">
            <div className="fs-result">
              <div className="fs-result-icon fs-result-icon--success">✓</div>
              <div className="fs-result-title">{tr('result_processing_title')}</div>
              <div className="fs-result-subtitle">{tr('result_processing_subtitle')}</div>
            </div>
          </div>
        </CheckoutShell>
      );
    }

    // failed / 其它：展示失败，但引导重新发起支付（去掉跳转参数刷新本页重新初始化）。
    const retryToCheckout = () => {
      const url = new URL(window.location.href);
      ['redirect_status', 'payment_intent', 'payment_intent_client_secret'].forEach((k) =>
        url.searchParams.delete(k)
      );
      window.location.replace(url.toString());
    };
    return (
      <CheckoutShell {...shellProps} merchantName={session?.merchant_name}>
        <div className="fs-card fs-card--single">
          <div className="fs-result">
            <div className="fs-result-icon fs-result-icon--warning">!</div>
            <div className="fs-result-title">{tr('result_failed_title')}</div>
            <div className="fs-result-subtitle">{tr('result_failed_subtitle')}</div>
            <div className="fs-result-actions">
              <Button type="primary" size="large" block onClick={retryToCheckout}>
                {tr('result_retry_pay')}
              </Button>
            </div>
          </div>
        </div>
      </CheckoutShell>
    );
  }

  // ---- 加载失败 ----
  if (loadError || !session) {
    return (
      <CheckoutShell {...shellProps}>
        <div className="fs-card fs-card--single fs-error-card">
          <div className="fs-result">
            <div className="fs-result-icon fs-result-icon--warning">!</div>
            <div className="fs-result-title">{tr('load_error_title')}</div>
            <div className="fs-result-subtitle">{loadError || tr('load_error_subtitle')}</div>
            <div className="fs-result-actions">
              <Button type="primary" size="large" block onClick={loadSession}>
                {tr('retry')}
              </Button>
            </div>
          </div>
        </div>
      </CheckoutShell>
    );
  }

  // ---- 会话已非 open（已完成 / 已过期 / 已取消） ----
  if (session.status !== 'open' && confirmState !== 'requires_action') {
    const conf = SESSION_STATUS_KEY[session.status];
    const statusTitle = conf ? tr(conf.title) : tr('session_unavailable_title');
    const statusSubtitle = conf ? tr(conf.subtitle) : tr('session_unavailable_subtitle');
    const isComplete = session.status === 'complete';
    return (
      <CheckoutShell {...shellProps} merchantName={session.merchant_name}>
        <div className="fs-card fs-card--single">
          <div className="fs-result">
            <div
              className={`fs-result-icon ${
                isComplete ? 'fs-result-icon--success' : 'fs-result-icon--warning'
              }`}
            >
              {isComplete ? '✓' : '!'}
            </div>
            <div className="fs-result-title">{statusTitle}</div>
            <div className="fs-result-subtitle">{statusSubtitle}</div>
          </div>
        </div>
      </CheckoutShell>
    );
  }

  // ---- 需要进一步验证（client_secret / next_action） ----
  if (confirmState === 'requires_action') {
    return (
      <CheckoutShell {...shellProps} merchantName={session.merchant_name}>
        <div className="fs-card fs-card--single">
          <div className="fs-result">
            <div className="fs-result-icon fs-result-icon--warning">!</div>
            <div className="fs-result-title">{tr('requires_action_title')}</div>
            <div className="fs-result-subtitle">{tr('requires_action_subtitle')}</div>
          </div>
        </div>
      </CheckoutShell>
    );
  }

  const isSetup = session.mode === 'setup';
  const isPayment = session.mode === 'payment';
  const currency = session.currency || 'USD';
  const modeKey = MODE_SUMMARY_KEY[session.mode];
  const modeText = modeKey ? tr(modeKey) : session.mode;
  const amountStr = formatMoney(session.amount_total, currency);
  const confirmText = isSetup
    ? tr('confirm_setup')
    : session.mode === 'subscription'
    ? tr('confirm_subscription', { amount: amountStr })
    : tr('confirm_payment', { amount: amountStr });

  return (
    <CheckoutShell {...shellProps} merchantName={session.merchant_name}>
      <div className="fs-card">
        {/* 左侧：订单摘要 */}
        <div className="fs-summary">
          <div className="fs-summary-label">{isSetup ? tr('summary_setup_label') : tr('summary_amount_due')}</div>
          <div className="fs-summary-amount">
            {isSetup ? tr('summary_setup_free') : formatMoney(session.amount_total, currency)}
          </div>
          <div className="fs-summary-currency">
            {isSetup ? tr('summary_setup_no_charge') : `${currency.toUpperCase()} · ${modeText}`}
          </div>

          {session.line_items?.map((item, index) => (
            <div className="fs-item" key={index}>
              <div>
                <div className="fs-item-name">
                  {item.name || tr('item_default_name')}
                  {item.quantity > 1 ? ` × ${item.quantity}` : ''}
                </div>
                {item.description && <div className="fs-item-desc">{item.description}</div>}
              </div>
              <div className="fs-item-amount">
                {formatMoney(item.unit_amount * (item.quantity || 1), item.currency || currency)}
              </div>
            </div>
          ))}

          {!isSetup && (
            <div className="fs-breakdown">
              <div className="fs-breakdown-row">
                <span>{tr('breakdown_subtotal')}</span>
                <span>{formatMoney(session.amount_subtotal, currency)}</span>
              </div>
              {session.amount_tax > 0 && (
                <div className="fs-breakdown-row">
                  <span>{tr('breakdown_tax')}</span>
                  <span>{formatMoney(session.amount_tax, currency)}</span>
                </div>
              )}
              <div className="fs-breakdown-total">
                <span className="fs-breakdown-total-label">{tr('breakdown_total')}</span>
                <span className="fs-breakdown-total-value">
                  {formatMoney(session.amount_total, currency)}
                </span>
              </div>
            </div>
          )}

          <CheckoutTrust lang={lang} />
        </div>

        {/* 右侧：确认区 */}
        <div className="fs-pay">
          <div className="fs-pay-title">
            {isSetup
              ? tr('pay_title_setup')
              : isPayment
              ? tr('pay_title_payment')
              : tr('pay_title_confirm')}
          </div>
          <div className="fs-pay-subtitle">
            {isSetup
              ? tr('pay_subtitle_setup')
              : session.mode === 'subscription'
              ? tr('pay_subtitle_subscription')
              : tr('pay_subtitle_payment')}
          </div>

          {session.customer_email && (
            <div className="fs-session-meta">
              <span className="fs-session-meta-key">{tr('customer_email')}</span>
              <span className="fs-session-meta-val">{session.customer_email}</span>
            </div>
          )}

          {/* payment 模式：单层选择器（Stripe / Paddle 风格）——
                默认主区：唯一 Stripe Payment Element（它自带的方式 tab/accordion 就是 Stripe 方式选择器，不再外包一层壳行）；
                自有渠道（epay/crypto）作为次级，放在 Element 下方，用分隔 + 紧凑小 tile 列出；
                互斥：同一时刻只显示 Stripe Element 或某个自有渠道，点自有渠道后给"返回用银行卡/钱包支付"链接切回。 */}
          {isPayment ? (
            <>
              {/* 加载方式清单中 */}
              {prepLoading && methods.length === 0 && (
                <div className="fs-card-input-loading">
                  <LoadingOutlined /> {tr('loading_component')}
                </div>
              )}

              {/* prepare 失败 */}
              {!prepLoading && methods.length === 0 && payError && (
                <div className="fs-card-input-error">{payError}</div>
              )}

              {/* Apple/Google Pay 钱包按钮：设备/浏览器支持且后台启用时出现在最上方（canMakePayment 门控） */}
              {hasCardMethod && (
                <div
                  className="fs-wallet"
                  style={walletAvailable ? undefined : { display: 'none' }}
                >
                  <div id="fs-wallet-button" className="fs-wallet-button" />
                  <div className="fs-wallet-divider">
                    <span>{tr('wallet_divider')}</span>
                  </div>
                </div>
              )}

              {/* 单层方式列表：信用卡 + 自有渠道（微信/支付宝/虚拟币）并列，每个一行，互斥展开 */}
              {methods.length > 0 && (
                <div className="fs-pm-list" role="radiogroup" aria-label={tr('pm_list_aria')}>
                  {hasCardMethod && (
                    <div
                      key="__card__"
                      className={`fs-pm-row${cardSelected ? ' fs-pm-row--active' : ''}`}
                      onClick={() => setSelectedPayKey(CARD_PAY_KEY)}
                      role="radio"
                      aria-checked={cardSelected}
                      tabIndex={0}
                      style={cardSelected ? { borderColor: '#2f6bff' } : undefined}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSelectedPayKey(CARD_PAY_KEY);
                        }
                      }}
                    >
                      <span className="fs-pm-brand">{renderFa(faCreditCard, '#2f6bff')}</span>
                      <span className="fs-pm-label">{tr('pm_card_label')}</span>
                      <span className="fs-pm-radio" />
                    </div>
                  )}
                  {ownMethods.map((m) => {
                    const vis = ownChannelVisual(m);
                    const active = selectedPayKey === m.method;
                    return (
                      <div
                        key={m.method}
                        className={`fs-pm-row${active ? ' fs-pm-row--active' : ''}`}
                        onClick={() => setSelectedPayKey(m.method)}
                        role="radio"
                        aria-checked={active}
                        tabIndex={0}
                        style={active ? { borderColor: vis.color } : undefined}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setSelectedPayKey(m.method);
                          }
                        }}
                      >
                        <span className="fs-pm-brand">{renderFa(vis.icon, vis.color)}</span>
                        <span className="fs-pm-label">{m.label}</span>
                        <span className="fs-pm-radio" />
                      </div>
                    );
                  })}
                  {/* 「更多支付方式」入口：仅当存在非卡非钱包的 Stripe 方式时显示（Payment Element 懒加载） */}
                  {hasMoreMethods && (
                    <div
                      key="__more__"
                      className={`fs-pm-row${moreSelected ? ' fs-pm-row--active' : ''}`}
                      onClick={() => setSelectedPayKey(MORE_PAY_KEY)}
                      role="radio"
                      aria-checked={moreSelected}
                      tabIndex={0}
                      style={moreSelected ? { borderColor: '#2f6bff' } : undefined}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSelectedPayKey(MORE_PAY_KEY);
                        }
                      }}
                    >
                      <span className="fs-pm-brand">{renderFa(faBuildingColumns, '#2f6bff')}</span>
                      <span className="fs-pm-label">
                        {tr('more_methods')}
                        <span className="fs-pm-sublabel">{tr('more_methods_subtitle')}</span>
                      </span>
                      <span className="fs-pm-radio" />
                    </div>
                  )}
                </div>
              )}

              {/* 选中「更多支付方式」：Stripe Payment Element（accordion）+ 确认按钮（'ready' gate） */}
              {hasMoreMethods && moreSelected && (
                <div className="fs-more-area">
                  {moreLoading && !moreReady && !moreError && (
                    <div className="fs-card-input-loading">
                      <LoadingOutlined /> {tr('loading_component')}
                    </div>
                  )}
                  {/* 容器必须先渲染，effect 内挂 Payment Element */}
                  <div id="fs-more-element" className="fs-more-element" />
                  {moreError && <div className="fs-card-input-error">{moreError}</div>}

                  <Button
                    type="primary"
                    size="large"
                    block
                    onClick={handlePayWithMore}
                    loading={processing}
                    disabled={!moreReady}
                    icon={processing ? <LoadingOutlined /> : <LockOutlined />}
                    style={{ marginTop: 14 }}
                  >
                    {processing
                      ? tr('btn_processing')
                      : !moreReady
                      ? tr('btn_loading_component')
                      : confirmText}
                  </Button>
                </div>
              )}

              {/* 选中信用卡：我们自己布局的拆分卡字段（卡号 / 有效期 / 安全码）+ 支付按钮。
                  卡号经 Stripe 隐藏 iframe，不进我们 DOM/服务器（PCI SAQ A）；外框样式全是我们自己的。 */}
              {hasCardMethod && cardSelected && (
                <div className="fs-stripe-area">
                  <div className="fs-card-input">
                    {!elementReady && !payError && (
                      <div className="fs-card-input-loading">
                        <LoadingOutlined /> {tr('loading_component')}
                      </div>
                    )}
                    {/* 三个容器必须先渲染，effect 内 mount 拆分卡字段 */}
                    <div className="fs-cardform">
                      <div className="fs-field-block">
                        <label className="fs-field-label">{tr('field_card_number')}</label>
                        <div className="fs-field" id="fs-card-number" />
                      </div>
                      <div className="fs-field-row">
                        <div className="fs-field-col">
                          <label className="fs-field-label">{tr('field_expiry')}</label>
                          <div className="fs-field" id="fs-card-expiry" />
                        </div>
                        <div className="fs-field-col">
                          <label className="fs-field-label">{tr('field_cvc')}</label>
                          <div className="fs-field" id="fs-card-cvc" />
                        </div>
                      </div>
                    </div>
                    {cardError && <div className="fs-card-input-error">{cardError}</div>}
                    {payError && <div className="fs-card-input-error">{payError}</div>}
                  </div>

                  <Button
                    type="primary"
                    size="large"
                    block
                    onClick={handlePayWithCard}
                    loading={processing}
                    disabled={!elementReady}
                    icon={processing ? <LoadingOutlined /> : <LockOutlined />}
                    style={{ marginTop: 14 }}
                  >
                    {processing
                      ? tr('btn_processing')
                      : !elementReady
                      ? tr('btn_loading_component')
                      : confirmText}
                  </Button>
                </div>
              )}

              {/* 自有渠道展开区：仅展开当前选中的那一个（互斥，点列表其它行即切换） */}
              {activeOwnMethod && (
                <div className="fs-own-panel">
                  <div className="fs-own-panel-title">{activeOwnMethod.label}</div>
                  {ownLoading && (
                    <div className="fs-card-input-loading">
                      <LoadingOutlined /> {tr('own_initiating')}
                    </div>
                  )}

                  {!ownLoading && ownError && (
                    <div className="fs-channel-tip">{ownError}</div>
                  )}

                  {/* 二维码渠道（微信 / 支付宝扫码） */}
                  {activeOwnType === 'qr' &&
                    !ownLoading &&
                    !ownError &&
                    ownResults[activeOwnMethod.method] && (
                      <div className="fs-qr-box">
                        {ownResults[activeOwnMethod.method].qr_content ? (
                          <>
                            {/* 本地 JS 渲染二维码（不再依赖外部 qrserver，二维码内容不出域）；
                                高纠错等级 H 容忍中心图标遮挡；中心叠加当前方式品牌图标。 */}
                            <div className="fs-qr-canvas">
                              <QRCodeSVG
                                value={ownResults[activeOwnMethod.method].qr_content || ''}
                                size={200}
                                level="H"
                                bgColor="#ffffff"
                                fgColor="#1a1f2e"
                                includeMargin
                              />
                              <span className="fs-qr-logo">
                                <FontAwesomeIcon
                                  icon={ownChannelVisual(activeOwnMethod).icon}
                                  style={{ color: ownChannelVisual(activeOwnMethod).color, fontSize: 22 }}
                                  aria-hidden="true"
                                />
                              </span>
                            </div>
                            <div className="fs-qr-tip">{tr('qr_scan_tip')}</div>
                            <div className="fs-poll-wait">
                              <LoadingOutlined /> {tr('poll_waiting')}
                            </div>
                          </>
                        ) : (
                          <div className="fs-channel-tip">{tr('qr_generating')}</div>
                        )}
                      </div>
                    )}

                  {/* 跳转渠道：已自动跳转，此处兜底手动按钮 */}
                  {activeOwnType === 'redirect' &&
                    !ownLoading &&
                    !ownError &&
                    ownResults[activeOwnMethod.method] && (
                      <div className="fs-channel-redirect">
                        <div className="fs-channel-tip">{tr('redirect_going')}</div>
                        {ownResults[activeOwnMethod.method].redirect_url && (
                          <Button
                            type="primary"
                            size="large"
                            block
                            onClick={() => {
                              const u = ownResults[activeOwnMethod.method].redirect_url;
                              if (u) window.location.href = u;
                            }}
                          >
                            {tr('redirect_manual')}
                          </Button>
                        )}
                      </div>
                    )}

                  {/* 加密货币渠道 */}
                  {activeOwnType === 'crypto' &&
                    !ownLoading &&
                    !ownError &&
                    ownResults[activeOwnMethod.method] && (
                      <div className="fs-crypto-box">
                        {ownResults[activeOwnMethod.method].crypto_address ? (
                          <>
                            <div className="fs-crypto-row">
                              <span className="fs-crypto-key">{tr('crypto_network')}</span>
                              <span className="fs-crypto-val">
                                {ownResults[activeOwnMethod.method].crypto_chain || '—'}
                              </span>
                            </div>
                            <div className="fs-crypto-row">
                              <span className="fs-crypto-key">{tr('crypto_amount')}</span>
                              <span className="fs-crypto-val">
                                {ownResults[activeOwnMethod.method].crypto_amount || '—'}
                              </span>
                            </div>
                            <div className="fs-crypto-addr-label">{tr('crypto_address_label')}</div>
                            <div className="fs-crypto-addr">
                              {ownResults[activeOwnMethod.method].crypto_address}
                            </div>
                            <Button
                              size="large"
                              block
                              onClick={() =>
                                handleCopyAddress(
                                  ownResults[activeOwnMethod.method].crypto_address || ''
                                )
                              }
                            >
                              {copied ? tr('copied') : tr('copy_address')}
                            </Button>
                            <div className="fs-qr-tip" style={{ marginTop: 10 }}>
                              {tr('crypto_transfer_tip')}
                            </div>
                            <div className="fs-poll-wait">
                              <LoadingOutlined /> {tr('poll_waiting')}
                            </div>
                          </>
                        ) : (
                          <div className="fs-channel-tip">{tr('crypto_preparing')}</div>
                        )}
                      </div>
                    )}
                </div>
              )}

              {/* 操作区：取消按钮 + 信任标识 */}
              <div className="fs-pay-actions">
                <Button size="large" block onClick={handleCancel} disabled={processing}>
                  {tr('cancel')}
                </Button>

                <div className="fs-pay-footer">
                  <div className="fs-trust-row">
                    <LockOutlined /> {tr('footer_secure')}
                  </div>
                </div>
              </div>
            </>
          ) : (
            // setup / subscription 模式：保留单一确认按钮
            <div className="fs-pay-actions">
              <Button
                type="primary"
                size="large"
                block
                onClick={handleConfirm}
                loading={processing}
                icon={processing ? <LoadingOutlined /> : <LockOutlined />}
              >
                {processing ? tr('btn_processing') : confirmText}
              </Button>
              <div style={{ marginTop: 10 }}>
                <Button size="large" block onClick={handleCancel} disabled={processing}>
                  {tr('cancel')}
                </Button>
              </div>

              <div className="fs-pay-footer">
                <div className="fs-trust-row">
                  <LockOutlined /> {tr('footer_secure')}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </CheckoutShell>
  );
};

const CheckoutPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // URL 带 ?token= 时走新收银会话流，否则保留旧 payment-intent 流
  const sessionToken = searchParams.get('token');
  const [paymentIntent, setPaymentIntent] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<string>('card');
  const [form] = Form.useForm();

  const embedded = isEmbedded();

  useEffect(() => {
    if (!sessionToken) {
      loadPaymentIntent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, sessionToken]);

  const loadPaymentIntent = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const response = await api.get(`/checkout/${id}`);
      if (response.data.success) {
        setPaymentIntent(response.data.data);
      } else {
        setLoadError('无法加载支付信息');
      }
    } catch (error: any) {
      setLoadError(error.response?.data?.message || '加载失败，订单可能不存在或已过期');
    } finally {
      setLoading(false);
    }
  };

  // 品牌 / 跳转字段（后端有则用，无则回退）
  const merchantName: string | undefined =
    paymentIntent?.merchant_name ||
    paymentIntent?.display_name ||
    paymentIntent?.merchant?.name;
  const logoUrl: string | undefined =
    paymentIntent?.logo_url || paymentIntent?.merchant?.logo_url;
  const successUrl: string | undefined =
    paymentIntent?.success_url || paymentIntent?.success_redirect_url;
  const cancelUrl: string | undefined =
    paymentIntent?.cancel_url || paymentIntent?.cancel_redirect_url;

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setProcessing(true);

      const response = await api.post(`/checkout/${id}/confirm`, {
        payment_method: {
          type: selectedMethod,
          card:
            selectedMethod === 'card'
              ? {
                  number: values.card_number,
                  exp_month: values.exp_month,
                  exp_year: values.exp_year,
                  cvc: values.cvc,
                }
              : undefined,
        },
      });

      if (response.data.success) {
        message.success('支付成功!');
        // 通知宿主环境（Flutter / iframe / 普通跳转）
        const handled = notifyCheckoutResult(
          'success',
          { payment_intent_id: id },
          successUrl
        );
        if (!handled) {
          navigate(`/checkout/success?payment_intent=${id}`);
        }
      } else {
        message.error('支付失败');
        notifyCheckoutResult('error', {
          payment_intent_id: id,
          message: '支付失败',
        });
      }
    } catch (error: any) {
      if (error.errorFields) {
        return; // 表单验证错误
      }
      const msg = error.response?.data?.message || '支付失败';
      message.error(msg);
      notifyCheckoutResult('error', { payment_intent_id: id, message: msg });
    } finally {
      setProcessing(false);
    }
  };

  const handleCancel = async () => {
    try {
      await api.post(`/checkout/${id}/cancel`);
      const handled = notifyCheckoutResult(
        'cancel',
        { payment_intent_id: id, reason: 'user_cancelled' },
        cancelUrl
      );
      if (!handled) {
        navigate(`/checkout/cancel?payment_intent=${id}`);
      }
    } catch (error: any) {
      message.error('取消失败');
    }
  };

  // ---- 新会话流（?token=） ----
  if (sessionToken) {
    return <CheckoutSessionFlow token={sessionToken} />;
  }

  // ---- 加载骨架屏 ----
  if (loading) {
    return (
      <CheckoutShell hideBrand={embedded}>
        <div className="fs-card">
          <div className="fs-summary">
            <div className="fs-skeleton" style={{ width: 90, height: 14, marginBottom: 14 }} />
            <div className="fs-skeleton" style={{ width: 180, height: 38, marginBottom: 24 }} />
            <div className="fs-skeleton" style={{ width: '100%', height: 56, marginBottom: 12 }} />
            <div className="fs-skeleton" style={{ width: '100%', height: 56 }} />
          </div>
          <div className="fs-pay">
            <div className="fs-skeleton" style={{ width: 120, height: 18, marginBottom: 22 }} />
            <div className="fs-skeleton" style={{ width: '100%', height: 52, marginBottom: 10 }} />
            <div className="fs-skeleton" style={{ width: '100%', height: 52, marginBottom: 10 }} />
            <div className="fs-skeleton" style={{ width: '100%', height: 52, marginBottom: 24 }} />
            <div className="fs-skeleton" style={{ width: '100%', height: 44 }} />
          </div>
        </div>
      </CheckoutShell>
    );
  }

  // ---- 订单不存在 / 已过期错误页 ----
  if (loadError || !paymentIntent) {
    return (
      <CheckoutShell hideBrand={embedded}>
        <div className="fs-card fs-card--single fs-error-card">
          <div className="fs-result">
            <div className="fs-result-icon fs-result-icon--warning">!</div>
            <div className="fs-result-title">无法打开收银台</div>
            <div className="fs-result-subtitle">
              {loadError || '该订单不存在或已过期，请返回商家重新发起支付。'}
            </div>
            <div className="fs-result-actions">
              <Button type="primary" size="large" block onClick={loadPaymentIntent}>
                重试
              </Button>
            </div>
          </div>
        </div>
      </CheckoutShell>
    );
  }

  const amount: number = paymentIntent.amount || 0;
  const currency: string = paymentIntent.currency || 'USD';
  const fee: number = paymentIntent.fee || 0;
  const tax: number = paymentIntent.tax || 0;
  const subtotal = amount - fee - tax;
  const total = amount;

  return (
    <CheckoutShell
      merchantName={merchantName}
      logoUrl={logoUrl}
      hideBrand={embedded}
    >
      <div className="fs-card">
        {/* 左侧：订单摘要 */}
        <div className="fs-summary">
          <div className="fs-summary-label">应付金额</div>
          <div className="fs-summary-amount">{formatMoney(total, currency)}</div>
          <div className="fs-summary-currency">{currency} · 一次性付款</div>

          <div className="fs-item">
            <div>
              <div className="fs-item-name">
                {paymentIntent.description || '订单付款'}
              </div>
              {paymentIntent.description && paymentIntent.product_name && (
                <div className="fs-item-desc">{paymentIntent.product_name}</div>
              )}
            </div>
            <div className="fs-item-amount">{formatMoney(subtotal, currency)}</div>
          </div>

          <div className="fs-breakdown">
            <div className="fs-breakdown-row">
              <span>小计</span>
              <span>{formatMoney(subtotal, currency)}</span>
            </div>
            {tax > 0 && (
              <div className="fs-breakdown-row">
                <span>税费</span>
                <span>{formatMoney(tax, currency)}</span>
              </div>
            )}
            {fee > 0 && (
              <div className="fs-breakdown-row">
                <span>手续费</span>
                <span>{formatMoney(fee, currency)}</span>
              </div>
            )}
            <div className="fs-breakdown-total">
              <span className="fs-breakdown-total-label">合计</span>
              <span className="fs-breakdown-total-value">
                {formatMoney(total, currency)}
              </span>
            </div>
          </div>

          <CheckoutTrust />
        </div>

        {/* 右侧：支付区 */}
        <div className="fs-pay">
          <div className="fs-pay-title">选择支付方式</div>
          <div className="fs-pay-subtitle">您的支付信息全程加密，安全可靠</div>

          <div className="fs-method-list">
            {PAYMENT_METHODS.map((m) => (
              <div
                key={m.type}
                className={`fs-method${selectedMethod === m.type ? ' fs-method--active' : ''}`}
                onClick={() => setSelectedMethod(m.type)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') setSelectedMethod(m.type);
                }}
              >
                <span className="fs-method-icon">{m.icon}</span>
                <span className="fs-method-label">{m.label}</span>
                <span className="fs-method-radio" />
              </div>
            ))}
          </div>

          <Form form={form} layout="vertical">
            {selectedMethod === 'card' && (
              <>
                <Form.Item
                  label="卡号"
                  name="card_number"
                  rules={[{ required: true, message: '请输入卡号' }]}
                >
                  <Input size="large" placeholder="1234 5678 9012 3456" maxLength={19} />
                </Form.Item>

                <div style={{ display: 'flex', gap: 12 }}>
                  <Form.Item
                    label="有效期 (月)"
                    name="exp_month"
                    style={{ flex: 1 }}
                    rules={[{ required: true, message: '请输入月份' }]}
                  >
                    <Input size="large" placeholder="MM" maxLength={2} />
                  </Form.Item>
                  <Form.Item
                    label="有效期 (年)"
                    name="exp_year"
                    style={{ flex: 1 }}
                    rules={[{ required: true, message: '请输入年份' }]}
                  >
                    <Input size="large" placeholder="YY" maxLength={2} />
                  </Form.Item>
                  <Form.Item
                    label="CVV"
                    name="cvc"
                    style={{ flex: 1 }}
                    rules={[{ required: true, message: '请输入CVV' }]}
                  >
                    <Input size="large" placeholder="123" maxLength={4} />
                  </Form.Item>
                </div>
              </>
            )}
          </Form>

          <div className="fs-pay-actions">
            <Button
              type="primary"
              size="large"
              block
              onClick={handleSubmit}
              loading={processing}
              icon={processing ? <LoadingOutlined /> : <LockOutlined />}
            >
              {processing ? '处理中...' : `确认支付 ${formatMoney(total, currency)}`}
            </Button>
            <div style={{ marginTop: 10 }}>
              <Button size="large" block onClick={handleCancel} disabled={processing}>
                取消
              </Button>
            </div>

            <div className="fs-pay-footer">
              <div className="fs-trust-row">
                <LockOutlined /> 安全加密支付
              </div>
              <div className="fs-trust-methods">
                <span className="fs-trust-pill">VISA</span>
                <span className="fs-trust-pill">MASTERCARD</span>
                <span className="fs-trust-pill">ALIPAY</span>
                <span className="fs-trust-pill">WECHAT</span>
              </div>
              <a className="fs-policy-link" href="#refund-policy" onClick={(e) => e.preventDefault()}>
                退款政策
              </a>
            </div>
          </div>
        </div>
      </div>
    </CheckoutShell>
  );
};

export default CheckoutPage;
